<template>
    <div v-if="series">
        <div v-if="loading" class="space-y-4">
            <Message status="info">{{ $t('loading-external-events') }}</Message>
        </div>
        <div v-if="!loading && noExternalEvents" class="space-y-4">
            <EmptyState>
                <template #description>
                    {{ $t('no-external-events') }}
                </template>
                <template #call-to-action>
                    <LinkButton
                        size="Base"
                        variant="Info-Strong"
                        :disabled-explanation="createEventDisabledExplanation"
                        :to="getCreateExternalEventLink(seriesId, series.nameShort, series.nameLong)"
                    >
                        <template #icon>
                            <PlusIcon class="size-5" />
                        </template>
                        {{ $t('external-event-add') }}
                    </LinkButton>
                </template>
            </EmptyState>
        </div>
        <div v-if="!loading && !noExternalEvents" class="space-y-4">
            <CollapsibleHeadline
                v-for="[grouping, periodEvents] in groupedExternalEvents"
                :key="grouping"
                :heading-level="3"
                :headline-id="grouping"
                :initial-is-open="['mifri', 'future'].includes(grouping)"
            >
                <template #headline>
                    {{ $t('external-event-grouping-label', { grouping: grouping, count: periodEvents.length }) }}
                </template>
                <template #content>
                    <div v-if="periodEvents.length > 0" class="space-y-2">
                        <ExternalEventCard
                            v-for="event in periodEvents"
                            :key="event.id"
                            :is-national="series.national"
                            :link-target="{ name: 'external-event-event', params: { eventId: event.id } }"
                            :link-text="event.nameShort"
                            :status-badge-variant="externalEventEventStatusToBadgeVariant(event.status)"
                            :status-badge-label="$t('external-event-event-status', { status: event.status })"
                            :variants-total-count="event.externalEventVariantsByEventId.totalCount"
                            :event-id="event.id"
                            :execution-year="event.year"
                            :variant="event.mainVariant"
                            :phase-types="externalEventPhaseTypes"
                            :link-to-reference-allocation="{
                                name: 'external-event-event-allocations-tab',
                                params: { eventId: event.id },
                            }"
                            :reference-allocation-query-document="ExternalEventAllocationPhaseTileDataDocument"
                            :reference-allocation-link-label="$t('phases-tile-title-reference-allocation-abbreviated')"
                        />
                    </div>
                    <div v-else class="space-y-2 font-light text-gray-600">({{ $t('no-events-in-this-period') }})</div>
                </template>
            </CollapsibleHeadline>
        </div>
    </div>
</template>
<script setup lang="ts">
import LinkButton from '@/components/Button/LinkButton.vue';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import Message from '@/components/Message/Message.vue';
import ExternalEventCard from '@/external_event/components/ExternalEventCard.vue';
import { useExternalEventSeriesData } from '@/external_event/composables/useExternalEventSeriesData';
import { useExternalEventSeriesInjection } from '@/external_event/composables/useExternalEventSeriesInjection';
import { externalEventEventStatusToBadgeVariant, getCreateExternalEventLink } from '@/external_event/externalEvent';
import { ExternalEventAllocationPhaseTileDataDocument } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { computed } from 'vue';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);

const { createEventDisabledExplanation } = useExternalEventSeriesInjection();

const { loading, series, noExternalEvents, externalEventPhaseTypes, groupedExternalEvents } =
    useExternalEventSeriesData(seriesId);
</script>
